/**
 * Photos from the media store come in resized WebP variants (`/api/v1/media/:id?w=480`, widths in
 * apps/api/src/services/media-variants.ts). Each place a photo appears asks for its slot's size:
 * a 380px card no longer downloads the 1600px original.
 */
export type PhotoSlot = "thumb" | "card" | "half" | "wide";

/** [1x, 2x] widths per slot; the server only renders these. */
export const SLOT_WIDTHS: Record<PhotoSlot, readonly [number, number]> = {
  thumb: [160, 320],
  card: [480, 960],
  half: [640, 1280],
  wide: [1280, 1920]
};

const MEDIA_PATH = /\/api\/v1\/media\/[0-9a-f-]{36}$/i;

/** The variant URL of a media-store photo; any other URL (a static asset, a data URI) as is. */
export function mediaVariant(url: string, width: number): string {
  return MEDIA_PATH.test(url) ? `${url}?w=${width}` : url;
}

/** `srcset` (width descriptors) for an <img>; undefined for photos outside the media store. */
export function slotSrcSet(url: string, slot: PhotoSlot): string | undefined {
  if (!MEDIA_PATH.test(url)) return undefined;
  const [small, large] = SLOT_WIDTHS[slot];
  return `${mediaVariant(url, small)} ${small}w, ${mediaVariant(url, large)} ${large}w`;
}

/** A CSS background image that picks the 1x or 2x variant for the screen's pixel density. */
export function slotBackground(url: string, slot: PhotoSlot): string {
  if (!MEDIA_PATH.test(url)) return `url("${url}")`;
  const [small, large] = SLOT_WIDTHS[slot];
  // The -webkit- spelling is the one every current engine accepts (Safari < 17 has no unprefixed one).
  return `-webkit-image-set(url("${mediaVariant(url, small)}") 1x, url("${mediaVariant(url, large)}") 2x)`;
}
